//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import EditMembers from './EditMemberList';

interface UserInfo {
  username: string;
  fullname: string;
  email?: string;
}

interface MemberListProps {
  loopName: string | undefined;
  selectedUsers: UserInfo[];
  searchQuery: string;
  handleRemoveUser: (username: string) => void;
  setSearchQuery: (query: string) => void;
  mode?: 'loop' | 'teamspace';
  userLoopMemberships: Map<string, string[]>;
  allUsers: UserInfo[];
  usersToAdd: UserInfo[];
  onSelectUser: (user: UserInfo) => void;
  onInviteEmail: (email: string) => void;
  newlyAddedUsernames?: Set<string>;
}

/**
 * Displays the list of current members with a search filter.
 *
 * @param {object} props The component props.
 * @param {string} [props.loopName] The name of the current loop or teamspace.
 * @param {UserInfo[]} props.selectedUsers The list of members to display.
 * @param {string} props.searchQuery The search filter query.
 * @param {function} props.handleRemoveUser Callback to remove a user.
 * @param {function} props.setSearchQuery Callback to update the search query.
 * @param {string} [props.mode] The display mode (loop/teamspace).
 * @param {UserInfo[]} props.allUsers The complete list of all users.
 * @returns {JSX.Element} The rendered MemberList component.
 */
const MemberList: React.FC<MemberListProps> = ({
  loopName,
  selectedUsers,
  searchQuery,
  handleRemoveUser,
  setSearchQuery,
  mode,
  userLoopMemberships,
  allUsers,
  usersToAdd,
  onSelectUser,
  onInviteEmail,
  newlyAddedUsernames,
}) => {
  const { t } = useTranslation();
  const [showTypeahead, setShowTypeahead] = useState(false);
  const typeaheadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredSelectedUsers = selectedUsers.filter(
    (user: UserInfo) =>
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.fullname.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const isValidEmail = (email: string) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const handleAddByEmail = () => {
    if (mode === 'teamspace' && isValidEmail(searchQuery)) {
      const isAlreadyAdded = selectedUsers.some(
        (user) => user.username.toLowerCase() === searchQuery.toLowerCase(),
      );
      if (!isAlreadyAdded) {
        onInviteEmail(searchQuery);
        setSearchQuery('');
        setShowTypeahead(false);
      }
    }
  };

  const isAlreadyMember =
    mode === 'teamspace' &&
    isValidEmail(searchQuery) &&
    selectedUsers.some(
      (user) => user.username.toLowerCase() === searchQuery.toLowerCase(),
    );

  const filteredUsersToAdd = usersToAdd.filter(
    (user) =>
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.fullname.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowTypeahead(value.trim().length > 0);
  };

  const handleSelectFromTypeahead = (user: UserInfo) => {
    onSelectUser(user);
    setSearchQuery('');
    setShowTypeahead(false);
  };

  const handleInviteFromTypeahead = () => {
    if (mode === 'teamspace' && isValidEmail(searchQuery)) {
      const isAlreadyAdded = selectedUsers.some(
        (user) => user.username.toLowerCase() === searchQuery.toLowerCase(),
      );
      if (!isAlreadyAdded) {
        onInviteEmail(searchQuery);
        setSearchQuery('');
        setShowTypeahead(false);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        typeaheadRef.current &&
        !typeaheadRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowTypeahead(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {/* Title + Tabs + Search grouped section */}
      <div
        id="id-member-list-section"
        data-testid="member-list-section"
        className="border-b border-border pb-4"
      >
        {/* Loop/Teamspace name + members label */}
        <div
          id="member-list-header"
          data-testid="member-list-header"
          className="flex items-center"
        >
          <div
            id="member-list-title"
            data-testid="member-list-title"
            className="flex gap-1.5"
          >
            <span
              id="member-list-loop-name"
              data-testid="member-list-loop-name"
              className="text-sm font-semibold text-foreground"
            >
              {loopName}
            </span>
            <span
              id="member-list-members-text"
              data-testid="member-list-members-text"
              className="text-sm font-medium text-foreground"
            >
              {mode === 'teamspace'
                ? t('teamspace-members')
                : t('loop-members')}
            </span>
          </div>
        </div>

        {/* Members List label */}
        <div
          id="member-list-tabs"
          data-testid="member-list-tabs"
          className="mt-4 flex items-center gap-6"
        >
          <span
            id="member-list-tab-members"
            data-testid="member-list-tab-members"
            className="border-b-2 border-primary pb-2 text-sm font-medium text-primary"
          >
            {t('MembersList')}
          </span>
        </div>

        {/* Search input with typeahead */}
        <div
          id="member-list-search-container"
          data-testid="member-list-search-container"
          className="relative mt-4 flex gap-2"
        >
          <div
            id="id-member-list-search-input-wrapper"
            data-testid="member-list-search-input-wrapper"
            className="relative flex-1"
          >
            <div
              id="id-member-list-search-icon"
              data-testid="member-list-search-icon"
              className="pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2"
            >
              <svg
                className="size-4 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              id="member-list-search-input"
              data-testid="id-add-members-search-input"
              ref={inputRef}
              type="text"
              placeholder={t('search-to-add', 'Search for members to add...')}
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => {
                if (searchQuery.trim().length > 0) setShowTypeahead(true);
              }}
              className="h-[30px] w-full rounded-md border border-input bg-background py-1 pl-8 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />

            {/* Typeahead dropdown */}
            {showTypeahead && searchQuery.trim().length > 0 && (
              <div
                ref={typeaheadRef}
                id="member-list-typeahead"
                data-testid="member-list-typeahead"
                className="absolute inset-x-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-md border border-border bg-card shadow-lg"
              >
                {filteredUsersToAdd.length > 0 ? (
                  filteredUsersToAdd.slice(0, 10).map((user) => (
                    <div
                      key={user.username}
                      id={`id-member-list-typeahead-user-${user.username}`}
                      data-testid={`member-list-typeahead-user-${user.username}`}
                      role="button"
                      tabIndex={0}
                      className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-secondary"
                      onClick={() => handleSelectFromTypeahead(user)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' && handleSelectFromTypeahead(user)
                      }
                    >
                      <div
                        id="id-member-list-typeahead-avatar"
                        data-testid="member-list-typeahead-avatar"
                        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground"
                      >
                        {user.fullname
                          ? user.fullname.charAt(0).toUpperCase()
                          : '?'}
                      </div>
                      <div
                        id="id-member-list-typeahead-user-info"
                        data-testid="member-list-typeahead-user-info"
                        className="flex min-w-0 flex-1 flex-col"
                      >
                        <span
                          id="id-member-list-typeahead-fullname"
                          data-testid="member-list-typeahead-fullname"
                          className="truncate text-sm font-medium text-foreground"
                        >
                          {user.fullname}
                        </span>
                        <span
                          id="id-member-list-typeahead-username"
                          data-testid="member-list-typeahead-username"
                          className="truncate text-xs text-muted-foreground"
                        >
                          {user.username}
                        </span>
                      </div>
                    </div>
                  ))
                ) : isAlreadyMember ? (
                  <div
                    id="id-member-list-already-member"
                    data-testid="member-list-already-member"
                    role="status"
                    aria-live="polite"
                    className="flex items-center gap-3 px-3 py-2 text-sm"
                  >
                    <div
                      aria-hidden="true"
                      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-yellow-100 text-xs font-medium text-yellow-700"
                    >
                      !
                    </div>
                    <span className="text-sm text-muted-foreground">
                      <strong>{searchQuery}</strong>{' '}
                      {t(
                        'already-a-member',
                        'is already a member of this teamspace',
                      )}
                    </span>
                  </div>
                ) : mode === 'teamspace' && isValidEmail(searchQuery) ? (
                  <div
                    id="id-member-list-invite-option"
                    data-testid="member-list-invite-option"
                    role="button"
                    tabIndex={0}
                    className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-secondary"
                    onClick={handleInviteFromTypeahead}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && handleInviteFromTypeahead()
                    }
                  >
                    <div
                      id="id-member-list-invite-icon"
                      data-testid="member-list-invite-icon"
                      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary"
                    >
                      +
                    </div>
                    <span
                      id="id-member-list-invite-text"
                      data-testid="member-list-invite-text"
                      className="text-sm text-foreground"
                    >
                      {t('invite-email', 'Invite')}{' '}
                      <strong>{searchQuery}</strong>
                    </span>
                  </div>
                ) : (
                  <div
                    id="id-member-list-no-results"
                    data-testid="member-list-no-results"
                    className="px-3 py-2 text-sm text-muted-foreground"
                  >
                    {t('no-results', 'No matching users found')}
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            id="member-list-add-button"
            data-testid="member-list-add-button"
            onClick={handleAddByEmail}
            disabled={
              !(mode === 'teamspace' && isValidEmail(searchQuery)) ||
              isAlreadyMember
            }
            className={`flex h-[30px] shrink-0 items-center justify-center rounded px-4 text-sm font-medium transition-colors ${
              mode === 'teamspace' &&
              isValidEmail(searchQuery) &&
              !isAlreadyMember
                ? 'cursor-pointer bg-primary text-primary-foreground hover:bg-primary-hover'
                : 'cursor-not-allowed bg-muted text-muted-foreground'
            }`}
          >
            {t('Add')}
          </button>
        </div>
      </div>

      {/* Scrollable member list */}
      <div
        id="member-list-scrollable"
        data-testid="member-list-scrollable"
        className="max-h-[700px] overflow-y-auto px-0 pb-12"
      >
        {filteredSelectedUsers.map((user: UserInfo) => {
          const isInvite =
            mode === 'teamspace' &&
            isValidEmail(user.username) &&
            !allUsers.some(
              (knownUser) =>
                knownUser.username.toLowerCase() ===
                  user.username.toLowerCase() ||
                (knownUser.email &&
                  knownUser.email.toLowerCase() ===
                    user.username.toLowerCase()),
            );
          return (
            <EditMembers
              key={user.username}
              fullname={user.fullname}
              username={user.username}
              onRemove={handleRemoveUser}
              isAdding={false}
              isInvite={isInvite}
              mode={mode}
              memberOfLoops={userLoopMemberships.get(user.username)}
              isNewlyAdded={newlyAddedUsernames?.has(user.username)}
            />
          );
        })}
      </div>
    </>
  );
};

export default MemberList;
